import { Box, Button, Tooltip } from '@mui/material';
import { DocumentType } from '@synergy/models';
import { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Formik, FormikProps } from 'formik';
import { PaloModal } from '@synergy/components-palo';
import { ScrollToFieldError } from '@synergy/components';
import { getUrlSegment } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import { useNavigate } from 'react-router-dom';

import { ConvertOutcome } from './models/convert-outcome';
import { ConvertReceiptCustomer } from './components/convert-receipt-customer';
import { ConvertReceiptHeader } from './components/convert-receipt-header';
import { ConvertReceiptTransitions } from './components/convert-receipt-transitions';
import { ConvertResult } from './components/convert-result';
import { ConverterJob } from './models/internal/converterJob';
import { ConverterJobFactory } from './util/converter-job-factory';
import { Transition } from './models/type-transition';
import { TransitionFactory } from './util/transition-factory';
import { TransitionSource } from './models/type-transition-source';
import { TransitionTarget } from './models/internal/transition-target';
import { runConversions } from './util/run-conversions';
import { validationSchema } from './validation-schema';

interface ConverterReceiptModalProps {
    isVisible: boolean;
    source: TransitionSource;
    /** Opens on this kind of follow-up, for the callers that already know it. */
    preselectedTarget?: TransitionTarget;
    onClose: () => void;
    /**
     * Reports what it created. A caller that answers nothing still works — the
     * result list then only says that the run went through.
     */
    onConvert?: (
        transition: Transition,
        target: TransitionTarget,
        source: DocumentType
    ) => void | Promise<ConvertOutcome | void>;
}

export const ConverterReceiptModal: FC<ConverterReceiptModalProps> = ({
    isVisible,
    onClose,
    source,
    onConvert,
    preselectedTarget,
}) => {
    const [converterJob, setConverterJob] = useState<ConverterJob | null>(null);
    const [outcome, setOutcome] = useState<ConvertOutcome | null>(null);
    const [running, setRunning] = useState(false);
    const formikRef = useRef<FormikProps<ConverterJob>>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const job = ConverterJobFactory.CreateConverterJob(source);

        if (job) {
            if (preselectedTarget && job.possibleTargets.includes(preselectedTarget)) {
                job.selectedTarget = preselectedTarget;
            }

            setConverterJob(job);
        }
    }, [preselectedTarget, source]);

    const handleConvert = () => {
        if (formikRef.current) {
            formikRef.current.submitForm();
        }
    };

    const handleSubmit = async (job: ConverterJob) => {
        if (!onConvert) {
            onClose?.();
            return;
        }

        setRunning(true);

        const outcome = await runConversions(TransitionFactory.createTransitions(job), transition =>
            onConvert(transition, job.selectedTarget, job.documentType)
        );

        setRunning(false);

        // One document made and nothing failed: straight into it, to finish it
        // there. The result window stood in between and showed a draft by its
        // internal id (#2316). It stays for several documents and for a failure,
        // which must not vanish behind a jump.
        const only = !outcome.failed && 1 === outcome.created.length ? outcome.created[0] : undefined;

        if (only) {
            onClose?.();
            navigate(`/${getUrlSegment(only.type)}/${only.id}`);

            return;
        }

        setOutcome(outcome);
    };

    const handleResultClose = () => {
        setOutcome(null);
        onClose?.();
    };

    const handleSelectAll = () => {
        const values = formikRef.current?.values;

        if (values && values.customers) {
            values.customers.forEach((customer, customerIndex) => {
                if (values.selectedCustomerId === -1 || customer.id === values.selectedCustomerId) {
                    customer.customerSuppliers.forEach((supplier, supplierIndex) => {
                        // "Alle auswählen" means all that may be carried on: a
                        // locked partner is not one of them (#2230).
                        if (customer.isLocked || supplier.isLocked) {
                            return;
                        }

                        supplier.items.forEach((item, itemIndex) => {
                            if (formikRef.current) {
                                formikRef.current.setFieldValue(
                                    `customers.${customerIndex}.customerSuppliers.${supplierIndex}.items.${itemIndex}.isSelected`,
                                    true
                                );
                            }
                        });
                    });
                }
            });
        }
    };

    // The result outlives the dialog it came from: the conversion is done, and
    // closing the form must not take the answer with it.
    if (outcome) {
        return <ConvertResult outcome={outcome} onClose={handleResultClose} />;
    }

    if (!isVisible) {
        return null;
    }

    if (!converterJob) {
        return null;
    }

    /*
     * The dialog opens with nothing picked, and creating then made nothing and
     * said so only afterwards. The button waits for a position instead.
     */
    const actionChildren = (values: ConverterJob) => {
        const nothingPicked = !values.customers.some(
            customer =>
                (values.selectedCustomerId === -1 || customer.id === values.selectedCustomerId) &&
                customer.customerSuppliers.some(supplier => supplier.items.some(item => item.isSelected))
        );

        // From an inquiry every supplier offers the same positions: "Alle
        // auswählen" ticked each of them at every supplier, and ordered it
        // several times over. The buyer picks per position (R-222).
        const fromInquiry = DocumentType.Inquiry === values.documentType;

        return [
            ...(fromInquiry && nothingPicked
                ? [
                      <Box key="hint" sx={{ mr: 'auto', fontSize: '12.5px', color: tokens.color.ink3 }}>
                          <FormattedMessage id="app.convertReceipt.pickSupplierPerPosition" />
                      </Box>,
                  ]
                : []),
            <Button key="1" variant="text" color="inherit" onClick={onClose} sx={{ color: tokens.color.ink2 }}>
                <FormattedMessage id="app.common.cancel" />
            </Button>,
            ...(fromInquiry
                ? []
                : [
                      <Button
                          key="2"
                          variant="text"
                          color="inherit"
                          onClick={handleSelectAll}
                          sx={{ color: tokens.color.ink2 }}
                      >
                          <FormattedMessage id="app.common.selectAll" />
                      </Button>,
                  ]),
            <Tooltip key="3" title={nothingPicked ? <FormattedMessage id="app.convertReceipt.nothingPicked" /> : ''}>
                <span>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleConvert}
                        disabled={running || nothingPicked}
                    >
                        <FormattedMessage id="app.menu.action.transition" />
                    </Button>
                </span>
            </Tooltip>,
        ];
    };

    return (
        <Formik
            innerRef={formikRef}
            onSubmit={handleSubmit}
            initialValues={converterJob}
            validationSchema={validationSchema}
            enableReinitialize={true}
        >
            {formikProps => (
                <>
                    <ScrollToFieldError />
                    <PaloModal
                        handleClose={onClose}
                        isVisible={isVisible}
                        actionChildren={actionChildren(formikProps.values)}
                        actionChildrenCentered={false}
                        title={<FormattedMessage id="app.convertReceipt.header" />}
                        titleComponent={
                            <ConvertReceiptHeader
                                possibleTargets={formikProps.values.possibleTargets}
                                converterCustomers={formikProps.values.customers}
                                deliveryDateRange={converterJob.deliveryDateRange}
                            />
                        }
                        width="lg"
                    >
                        <ConvertReceiptTransitions source={source} />
                        {formikProps.values.customers.map((customer, index) => {
                            if (
                                formikProps.values.selectedCustomerId === -1 ||
                                customer.id === formikProps.values.selectedCustomerId
                            ) {
                                return (
                                    <ConvertReceiptCustomer
                                        key={customer.id}
                                        converterCustomer={customer}
                                        converterCustomerIndex={index}
                                        showCustomer={formikProps.values.selectedCustomerId < 0}
                                        showCustomerAsHeader={converterJob.documentType === DocumentType.Demand}
                                        formikProps={formikProps}
                                        showPrice={formikProps.values.selectedTarget !== TransitionTarget.Inquiry}
                                        showDiscount={formikProps.values.documentType === DocumentType.PurchaseOrder}
                                        showTax={formikProps.values.documentType === DocumentType.PurchaseOrder}
                                        showDeliveryCosts={formikProps.values.supportsDeliveryCosts}
                                    />
                                );
                            }
                            return null;
                        })}
                    </PaloModal>
                </>
            )}
        </Formik>
    );
};
